import React, { useState, useEffect } from 'react'
import {
  Modal,
  ModalHeader,
  ModalContent,
  ModalFooter,
  Button,
  Flex,
  Box,
  Text,
  Loader,
  Checkbox,
  Icon,
} from '@vibe/core'
import { Person } from '@vibe/icons'
import type {
  WorkflowConfiguration,
  CreateWorkflowRequest,
  UpdateWorkflowRequest,
  UserInfo,
} from '../../types'
import { ApiService } from '../../services/api'

interface SimpleWorkflowBuilderProps {
  workflow?: WorkflowConfiguration | null
  onSubmit: () => void
  onCancel: () => void
  apiService: ApiService
}

export const SimpleWorkflowBuilder: React.FC<SimpleWorkflowBuilderProps> = ({
  workflow,
  onSubmit,
  onCancel,
  apiService,
}) => {
  // Form state - simplified without name and description
  const [selectedApprovers, setSelectedApprovers] = useState<string[]>([])

  // Data state
  const [users, setUsers] = useState<UserInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isEditing = !!(workflow && workflow.id)

  // Load users on mount
  useEffect(() => {
    loadUsers()
  }, [])

  // Debug effect to monitor state changes
  useEffect(() => {
    console.log('selectedApprovers changed:', selectedApprovers)
  }, [selectedApprovers])

  // Extract current approvers from workflow and filter out deleted users
  useEffect(() => {
    console.log('Workflow or users changed:', {
      workflow: !!workflow,
      usersCount: users.length,
    })
    if (workflow?.workflow_logic && users.length > 0) {
      const logic = workflow.workflow_logic
      console.log('Workflow logic:', logic)
      let approverIds: string[] = []

      if (logic.type === 'USER' && logic.user_id) {
        // Single user workflow
        approverIds = [logic.user_id]
        console.log('Single user workflow, approverIds:', approverIds)
      } else if (logic.conditions) {
        // AND/OR workflow with conditions
        const userConditions = logic.conditions.filter(
          (condition: any) => condition.type === 'USER'
        )
        approverIds = userConditions
          .map((condition: any) => condition.user_id)
          .filter(Boolean) // Remove any undefined/null values
        console.log('Multi-user workflow, approverIds:', approverIds)
      }

      // Filter out deleted users - only keep IDs that exist in current users list
      const validUserIds = users.map(user => user.id)
      const filteredApproverIds = approverIds.filter(id =>
        validUserIds.includes(id)
      )

      console.log('Original approverIds:', approverIds)
      console.log('Valid user IDs:', validUserIds)
      console.log(
        'Filtered approverIds (removing deleted users):',
        filteredApproverIds
      )

      if (filteredApproverIds.length !== approverIds.length) {
        console.warn(
          `Removed ${approverIds.length - filteredApproverIds.length} deleted users from workflow`
        )
      }

      setSelectedApprovers(filteredApproverIds)
    }
  }, [workflow, users])

  const loadUsers = async () => {
    try {
      setLoading(true)
      const response = await apiService.getUsers()
      const loadedUsers = response.data.items || []
      console.log('Loaded users:', loadedUsers)
      console.log(
        'Users with id:',
        loadedUsers.map(u => ({
          name: u.name,
          id: u.id,
        }))
      )
      setUsers(loadedUsers)
    } catch (err) {
      setError('Error al cargar usuarios')
      console.error('Error loading users:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleApproverToggle = (userId: string) => {
    console.log('Toggle called with userId:', userId)
    console.log('Current selectedApprovers:', selectedApprovers)

    if (!userId || userId.trim() === '') {
      console.error('Invalid userId provided to toggle:', userId)
      return
    }

    setSelectedApprovers(prev => {
      const newSelection = prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
      console.log('New selection:', newSelection)
      return newSelection
    })
  }

  const handleSubmit = async () => {
    if (selectedApprovers.length === 0) {
      setError('Debe seleccionar al menos un aprobador')
      return
    }

    // Validate that all selected approvers have valid values
    const invalidApprovers = selectedApprovers.filter(
      approver => !approver || approver.trim() === ''
    )
    if (invalidApprovers.length > 0) {
      setError(
        'Error: algunos aprobadores seleccionados no tienen un ID válido'
      )
      return
    }

    // Additional validation: ensure all selected approvers still exist in the users list
    const validUserIds = users.map(user => user.id)
    const deletedApprovers = selectedApprovers.filter(
      id => !validUserIds.includes(id)
    )
    let finalApprovers = selectedApprovers

    if (deletedApprovers.length > 0) {
      console.warn('Removing deleted users from selection:', deletedApprovers)
      finalApprovers = selectedApprovers.filter(id => validUserIds.includes(id))
      setSelectedApprovers(finalApprovers)

      if (finalApprovers.length === 0) {
        setError(
          'Los usuarios seleccionados ya no existen. Por favor, selecciona usuarios válidos.'
        )
        return
      }

      // Show warning but continue with valid approvers
      console.log(
        `Continuing with ${finalApprovers.length} valid approvers out of ${selectedApprovers.length} originally selected`
      )
    }

    try {
      setSubmitting(true)
      setError(null)

      // Build workflow logic using filtered approvers
      let workflowLogic
      if (finalApprovers.length === 1) {
        // Single user - use USER type directly
        workflowLogic = {
          type: 'USER' as const,
          user_id: finalApprovers[0],
        }
      } else {
        // Multiple users - use AND logic
        workflowLogic = {
          type: 'AND' as const,
          conditions: finalApprovers.map(userId => ({
            type: 'USER' as const,
            user_id: userId,
          })),
        }
      }

      if (workflow && workflow.id) {
        // Update existing workflow
        const updateRequest: UpdateWorkflowRequest = {
          name: 'Flujo de Aprobacion Principal',
          description:
            'Flujo principal para aprobacion de solicitudes de trabajo',
          workflow_logic: workflowLogic,
          is_active: 'true',
        }
        await apiService.updateWorkflow(workflow.id, updateRequest)
      } else {
        // Create new workflow
        const createRequest: CreateWorkflowRequest = {
          name: 'Flujo de Aprobacion Principal',
          description:
            'Flujo principal para aprobacion de solicitudes de trabajo',
          workflow_logic: workflowLogic,
        }
        await apiService.createWorkflow(createRequest)
      }

      onSubmit()
    } catch (err) {
      setError('Error al guardar el flujo de aprobación')
      console.error('Error saving workflow:', err)
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <Modal show={true} onClose={onCancel} title="Cargando configuración">
        <ModalContent>
          <Flex align="center" justify="center" style={{ padding: '40px' }}>
            <Loader size="large" />
            <Text style={{ marginLeft: '16px' }}>Cargando usuarios...</Text>
          </Flex>
        </ModalContent>
      </Modal>
    )
  }

  return (
    <Modal show={true} onClose={onCancel} data-testid="workflow-builder-modal">
      <ModalHeader>
        <Text weight="bold" style={{ fontSize: '20px' }}>
          {isEditing ? 'Configurar Aprobadores' : 'Activar Flujo de Aprobación'}
        </Text>
      </ModalHeader>

      <ModalContent>
        <Flex direction="column" gap="large">
          {/* Approvers Selection */}
          <Box>
            <Text weight="medium" className="mb-3">
              Seleccionar Aprobadores
            </Text>
            <Text type="text3" color="secondary" className="mb-4">
              {selectedApprovers.length <= 1
                ? 'Las solicitudes de trabajo requerirán aprobación del usuario seleccionado'
                : 'Las solicitudes de trabajo requerirán aprobación de TODOS los usuarios seleccionados'}
            </Text>

            <div className="space-y-2 max-h-60 overflow-y-auto border rounded p-3">
              {users
                .filter(user => user.id && user.id.trim() !== '')
                .map((user, index) => {
                  const isSelected = selectedApprovers.includes(user.id)
                  console.log(
                    `User ${user.name} (${user.id}) - Selected: ${isSelected}`
                  )
                  return (
                    <div
                      key={user.id || `user-${index}`}
                      className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded"
                    >
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleApproverToggle(user.id)}
                        disabled={submitting}
                      />
                      <Icon icon={Person} iconSize={16} />
                      <div className="flex-1">
                        <Text weight="medium">{user.name || user.email}</Text>
                        <Text type="text3" color="secondary">
                          {user.email}
                        </Text>
                        <Text
                          type="text3"
                          color="secondary"
                          style={{ fontSize: '12px' }}
                        >
                          ID: {user.id}
                        </Text>
                      </div>
                    </div>
                  )
                })}
            </div>

            {selectedApprovers.length > 0 && (
              <Text type="text3" color="secondary" className="mt-2">
                {selectedApprovers.length} aprobador(es) seleccionado(s)
              </Text>
            )}
          </Box>

          {/* Error Message */}
          {error && (
            <Box>
              <Text color="negative">{error}</Text>
            </Box>
          )}
        </Flex>
      </ModalContent>

      <ModalFooter>
        <Flex gap="medium" justify="end">
          <Button kind="tertiary" onClick={onCancel} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || selectedApprovers.length === 0}
            loading={submitting}
          >
            {isEditing ? 'Actualizar Configuración' : 'Activar Flujo'}
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  )
}
